import { useState, useEffect, useRef } from 'react';
import AsciiField from './components/AsciiField';
import WordmarkLiving from './components/WordmarkLiving';
import UnifiedField from './components/UnifiedField';
import PaintWindow from './components/PaintWindow';

const FONT_M    = "'Space Mono', monospace";
const FONT_NDOT = "'Ndot55', monospace";

/* ─── ASCII REVEAL MAP ─── */
const ASCII_MAP = {
  A: '/\\', B: '|3',    C: '(—',    D: '|)',   E: '|=',
  F: '|—',  G: '(9',    H: '|-|',   I: '|',    J: '_|',
  K: '|',   L: '|_',    M: '|\\/|', N: '|\\|', O: '()',
  P: '|D',  Q: '(9',    R: '|2',    S: '$',    T: '-|-',
  U: '|_|', V: '\\/',   W: '\\X/',  X: '>',    Y: '-/',
  Z: '2_',  ' ': ' ',   '/': '/',   '-': '-',
};

/* Deterministic per-letter ASCII/Ndot55 split — seeded on projectId+index */
function deterministicUseAscii(projectId, idx) {
  let h = idx * 31;
  for (let i = 0; i < projectId.length; i++) {
    h = (h * 31 + projectId.charCodeAt(i)) & 0xffffffff;
  }
  return Math.abs(h) % 2 === 0;
}

/* ─── PROJECT DATA ─── */
const PROJECTS = {
  stacy: {
    pos: { left: '42vw', top: '26vh' },
    chain: 'ETH · ORIGIN',
    name: ['STACY', 'PILLS'],
    stat1: '14 DESIGNS. 30 MINUTES. SOLD OUT.',
    stat2: 'SI + SII · 10 COLLABORATORS · 2021—2022',
    link: 'https://rarible.com/stacypills',
    image: 'https://pbs.twimg.com/ext_tw_video_thumb/1455884533958467594/pu/img/Ke6c6wya0NZbbDmC.jpg',
  },
  pillheads: {
    pos: { left: '72vw', top: '36vh' },
    chain: 'ETH · MAIN COLLECTION',
    name: ['PILL', 'HEADS'],
    stat1: '6,655 CITIZENS. BORN ON-CHAIN.',
    stat2: '1,450+ HOLDERS · 2022',
    link: 'https://opensea.io/collection/pillheadsnft',
    image: 'https://pbs.twimg.com/media/Fb3gE9AaQAERf0P.png',
  },
  palette: {
    pos: { left: '38vw', top: '68vh' },
    chain: 'ETH · ERC-404',
    name: ['PALETTE', '/ PLT'],
    stat1: 'FIRST GENERATIVE ART ON ERC-404.',
    stat2: 'FULLY ON-CHAIN · 2024',
    link: 'https://x.com/palettebycfw',
    image: 'https://pbs.twimg.com/media/GGacZGMXoAAQ429.jpg',
  },
  repillca: {
    pos: { left: '62vw', top: '20vh' },
    chain: 'BTC · ORDINALS',
    name: ['RE', 'PILLCA'],
    stat1: '200 INSCRIPTIONS. THE PILL, ABSTRACTED.',
    stat2: 'BITCOIN ORDINALS · 2023',
    link: 'https://gamma.io/ordinals/collections/repillca/items',
    image: 'https://pbs.twimg.com/ext_tw_video_thumb/1637826387527573505/pu/img/DaZAv_LboWbbPjT-.jpg',
  },
  sorrow: {
    pos: { left: '55vw', top: '52vh' },
    chain: 'ETH · OPEN EDITION',
    name: ['SORROW', 'OF CHOICE'],
    stat1: '19K MINTED. 12K BURNED.',
    stat2: 'BOUNDLESS EXPERIMENTATION',
    link: 'https://sorrowofchoice.carrd.co/',
    image: 'https://pbs.twimg.com/ext_tw_video_thumb/1625928092815613952/pu/img/iIQdJrpWD8ZfWhAg.jpg',
  },
  physical: {
    pos: { left: '68vw', top: '70vh' },
    chain: 'IRL · DESIGNER TOY',
    name: ['THE', 'PILLHEAD'],
    stat1: '111 UNITS. RESIN. SHIPPED WORLDWIDE.',
    stat2: 'DESIGNER TOY Nº1 · 2023',
    link: 'https://x.com/iamcfw/status/1696575268779946238',
    image: 'https://pbs.twimg.com/ext_tw_video_thumb/1696573528517079044/pu/img/kha_1gLxKh0aL-P8.jpg',
  },
  cfw_market: {
    pos: { left: '22vw', top: '38vh' },
    chain: 'ART. MARKETS. BELIEF',
    name: ['CFW', 'MARKET'],
    stat1: 'ART PORTFOLIO AND TRADE JOURNAL.',
    stat2: 'DIGITAL ART · CRYPTO MARKETS · ONGOING',
    link: 'https://cfw.market',
    ctaLabel: 'VISIT SITE',
    image: '/images/cfw_a.jpg',
  },
};

const PROJECT_IDS = ['stacy', 'pillheads', 'palette', 'repillca', 'sorrow', 'physical', 'cfw_market'];

/* ─── Deterministic micro-offset per node on hover ─── */
const HOVER_NUDGE = {
  stacy:      { x:  2, y: -5 },
  pillheads:  { x: -3, y: -4 },
  palette:    { x:  3, y: -5 },
  repillca:   { x: -2, y: -4 },
  sorrow:     { x:  2, y: -5 },
  physical:   { x: -3, y: -4 },
  cfw_market: { x:  2, y: -5 },
};

const CONNECT_LINKS = [
  { label: 'X',          url: 'https://x.com/iamcfw' },
  { label: 'WARPCAST',   url: 'https://warpcast.com/iamcfw' },
  { label: 'OPENSEA',    url: 'https://opensea.io/iamCFW' },
  { label: 'FOUNDATION', url: 'https://foundation.app/@iamcfw' },
  { label: 'CFW.MARKET', url: 'https://cfw.market' },
];

/* ─── HOOKS ─── */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [setIsMobile]); // setIsMobile is a stable useState setter
  return isMobile;
}

/* ─── CUSTOM CURSOR ─── */
function CustomCursor() {
  const dotRef  = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const x = e.clientX;
      const y = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.left = `${x - 3}px`;
        dotRef.current.style.top  = `${y - 3}px`;
      }
      if (ringRef.current) {
        ringRef.current.style.left = `${x - 12}px`;
        ringRef.current.style.top  = `${y - 12}px`;
      }
    };

    const onOver = (e) => {
      const interactive = !!e.target.closest('button, a, input, [data-interactive]');
      if (ringRef.current) {
        ringRef.current.style.opacity = interactive ? '1' : '0';
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseover', onOver, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
    };
    // dotRef/ringRef are stable refs; handlers are defined inside this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* 6×6 square dot */}
      <div
        ref={dotRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          width: 6,
          height: 6,
          background: '#0D0D0D',
          zIndex: 9999,
          pointerEvents: 'none',
          top: -100,
          left: -100,
        }}
      />
      {/* 24px outer ring */}
      <div
        ref={ringRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          width: 24,
          height: 24,
          border: '1px solid #0D0D0D',
          zIndex: 9998,
          pointerEvents: 'none',
          top: -100,
          left: -100,
          opacity: 0,
        }}
      />
    </>
  );
}

/* ─── LAYER 2 — STATIC META ─── */
function StaticMeta() {
  const isMobile = useIsMobile();
  const metaStyle = {
    fontFamily: FONT_M,
    fontSize: 9,
    color: '#0D0D0D',
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    lineHeight: 1.8,
  };

  return (
    <>
      {/* Top-right metadata — hidden on mobile (wordmark subline covers it) */}
      {!isMobile && (
        <div
          data-testid="meta-topright"
          style={{
            position: 'absolute',
            top: 20,
            right: 24,
            zIndex: 10,
            textAlign: 'right',
            opacity: 0.72,
            pointerEvents: 'none',
            ...metaStyle,
          }}
        >
          <div>PILLHEADS.ART</div>
          <div>EST. 2021</div>
          <div>ETH · BTC · IRL</div>
        </div>
      )}

      {/* Bottom-left signature */}
      <div
        style={{
          position: 'absolute',
          bottom: isMobile ? 'calc(72px + env(safe-area-inset-bottom, 0px))' : 20,
          left: 24,
          zIndex: 10,
          opacity: 0.72,
          pointerEvents: 'none',
          ...metaStyle,
        }}
      >
        <div data-testid="signature-trigger">@IAMCFW</div>
        {/* Long tagline hidden on mobile to prevent overlap with connect area */}
        {!isMobile && <div>A UNIVERSE OF PILLS, CHARACTERS + GEOMETRY</div>}
      </div>

      {/* Bottom-right label — hidden on mobile */}
      {!isMobile && (
        <div style={{
          position: 'absolute',
          bottom: 20,
          right: 24,
          zIndex: 10,
          opacity: 0.38,
          ...metaStyle,
          fontSize: 8,
          pointerEvents: 'none',
        }}>
          // STACYVERSE.EXE
        </div>
      )}
    </>
  );
}

/* ─── HELPERS ─── */
function getNodeOpacity(active, hovered, id) {
  if (active === id)                         return 0;
  if (active && active !== id)               return 0.08;
  if (hovered === id && !active)             return 1.0;
  if (hovered && hovered !== id && !active)  return 0.15;
  return 0.65;
}

/* ─── LAYER 3 — PROJECT NODE ─── */
function ProjectNode({ id, hovered, active, onHover, onLeave, onClick, isMobile, index, mousePos, disabled }) {
  const project        = PROJECTS[id];
  const isHovered      = hovered === id && !active;
  const isFullyIdle    = !active && !hovered;
  const touchTimerRef  = useRef(null);

  /* Base opacity */
  let opacity = getNodeOpacity(active, hovered, id);
  let nudgeX  = 0;
  let nudgeY  = 0;

  if (isHovered && !isMobile) {
    /* Micro-position offset on hover — deterministic per node */
    const off = HOVER_NUDGE[id] || { x: 0, y: -4 };
    nudgeX = off.x;
    nudgeY = off.y;
  } else if (isFullyIdle && !isMobile && mousePos) {
    /* Proximity effect — only when fully idle on desktop */
    const nodeX = parseFloat(project.pos.left) / 100 * window.innerWidth;
    const nodeY = parseFloat(project.pos.top)  / 100 * window.innerHeight;
    const dist  = Math.sqrt((mousePos.x - nodeX) ** 2 + (mousePos.y - nodeY) ** 2);
    const PROX_R = Math.min(window.innerWidth * 0.14, 220);
    const prox  = Math.max(0, 1 - dist / PROX_R);
    opacity = 0.45 + prox * 0.25;
    nudgeY  = -prox * 2;
  }

  const position = isMobile
    ? { left: '50%', top: `${20 + index * 10}vh` }
    : { left: project.pos.left, top: project.pos.top };

  return (
    <button
      data-testid={`node-${id}`}
      onClick={(e) => { e.stopPropagation(); onClick(id); }}
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        onHover(id, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }}
      onMouseLeave={() => onLeave()}
      onTouchStart={(e) => {
        e.preventDefault();
        const t = e.touches[0];
        onHover(id, { x: t?.clientX || 0, y: t?.clientY || 0 });
        clearTimeout(touchTimerRef.current);
        touchTimerRef.current = setTimeout(() => onClick(id), 300);
      }}
      onTouchEnd={() => {
        clearTimeout(touchTimerRef.current);
        if (hovered === id) { onClick(id); onLeave(); }
      }}
      onTouchCancel={() => { clearTimeout(touchTimerRef.current); onLeave(); }}
      style={{
        position: 'absolute',
        background: 'none',
        border: 'none',
        padding: '4px 0',
        zIndex: 20,
        opacity,
        transform: isMobile
          ? 'translateX(-50%)'
          : `translate(${nudgeX.toFixed(2)}px, ${nudgeY.toFixed(2)}px)`,
        transition: 'opacity 80ms steps(2), transform 120ms steps(3)',
        textAlign: isMobile ? 'center' : 'left',
        pointerEvents: disabled ? 'none' : 'auto',
        ...position,
      }}
    >
      <div style={{
        fontFamily: FONT_M,
        fontSize: 10,
        color: 'rgba(13,13,13,0.25)',
        textTransform: 'uppercase',
        letterSpacing: '0.25em',
        marginBottom: 2,
      }}>
        {project.chain.split('·')[0].trim()} ·
      </div>
      <div style={{
        fontFamily: FONT_M,
        fontSize: isHovered ? 15 : 13,
        color: '#0D0D0D',
        textTransform: 'uppercase',
        letterSpacing: '0.25em',
        transition: 'font-size 80ms steps(2)',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}>
        {project.name.join(' ')}
      </div>
    </button>
  );
}

/* ─── COLLECTION LINK — extracted from ActiveContent ─── */
function CollectionLink({ href, projectId, label = 'VIEW COLLECTION' }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`link-${projectId}`}
      style={{
        fontFamily: FONT_M,
        fontSize: 11,
        color: 'rgba(13,13,13,0.7)',
        textTransform: 'uppercase',
        letterSpacing: '0.2em',
        borderBottom: '1px solid rgba(13,13,13,0.4)',
        textDecoration: 'none',
        display: 'inline-block',
        paddingBottom: 2,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#0D0D0D';
        e.currentTarget.style.borderBottomColor = '#0D0D0D';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'rgba(13,13,13,0.7)';
        e.currentTarget.style.borderBottomColor = 'rgba(13,13,13,0.4)';
      }}
    >
      {label} ↗
    </a>
  );
}

/* ─── TITLE FONT SIZE — scales with longest word length ─── */
function titleFontVw(name) {
  const longestWordChars = Math.max(...name.map(w => w.replace(/\s/g, '').length));
  const REF = 5, BASE = 8;
  return Math.max(4, Math.min(9, BASE * (REF / Math.max(longestWordChars, REF))));
}

/* ─── ASCII TITLE RENDERER ─── */
function AsciiTitle({ name, frame, projectId }) {
  const fvw = titleFontVw(name);
  return (
    <>
      {name.map((word, wordIdx) => {
        const offset = name.slice(0, wordIdx).reduce((sum, w) => sum + w.length, 0);
        return (
          <div
            key={word}
            style={{
              fontFamily: FONT_NDOT,
              fontSize: `${fvw}vw`,
              color: '#0D0D0D',
              lineHeight: 0.88,
              letterSpacing: '-0.01em',
              marginLeft: wordIdx === 1 ? '1.5vw' : 0,
              display: 'block',
              overflow: 'visible',
            }}
          >
            {word.split('').map((letter, li) => {
              const gIdx     = offset + li;
              const useAscii = frame === 0
                ? true
                : frame === 2
                  ? false
                  : deterministicUseAscii(projectId, gIdx);
              const glyph = useAscii ? (ASCII_MAP[letter] ?? letter) : letter;
              return (
                <span
                  key={li}
                  style={{
                    fontFamily:    useAscii ? FONT_M    : FONT_NDOT,
                    fontSize:      useAscii ? `${fvw * 0.5}vw` : 'inherit',
                    verticalAlign: 'baseline',
                    letterSpacing: useAscii ? '0.02em'  : undefined,
                  }}
                >
                  {glyph}
                </span>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

/* ─── LAYER 4 — ACTIVE CONTENT ─── */
function ActiveContent({ projectId, onClose, isMobile, contentRef }) {
  const project = PROJECTS[projectId];
  const [titleFrame, setTitleFrame] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setTitleFrame(1), 80);
    const t2 = setTimeout(() => setTitleFrame(2), 160);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  let posStyle;
  if (isMobile) {
    posStyle = { left: 0, right: 0, top: '20vh' };
  } else {
    const leftVal      = parseFloat(project.pos.left);
    const topVal       = parseFloat(project.pos.top);
    const CONTENT_W_VW = 28;

    let contentLeft = leftVal > 58 ? leftVal - CONTENT_W_VW - 2 : leftVal;
    let contentTop  = topVal  > 45 ? Math.max(2, topVal - 55)   : topVal + 3;

    contentLeft = Math.max(1, contentLeft);
    if (contentLeft + CONTENT_W_VW > 97) {
      contentLeft = 97 - CONTENT_W_VW;
    }
    contentTop = Math.max(2, Math.min(contentTop, 85));

    posStyle = { left: `${contentLeft}vw`, top: `${contentTop}vh`, width: `${CONTENT_W_VW}vw`, maxWidth: `${CONTENT_W_VW}vw` };
  }

  const reveal = (delay) => ({
    opacity: 0,
    animation: `lineReveal 0.15s steps(2) ${delay}ms forwards`,
  });

  return (
    <div
      ref={contentRef}
      data-testid={`active-${projectId}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        zIndex: 30,
        background: 'rgba(242, 239, 228, 0.92)',
        borderLeft: '1px solid rgba(13,13,13,0.10)',
        borderTop: '1px solid rgba(13,13,13,0.10)',
        padding: '20px 24px 28px',
        overflow: 'hidden',
        ...posStyle,
      }}
    >
      {/* Header row: chain label + ESC */}
      <div style={{
        ...reveal(0),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 10,
      }}>
        <div style={{
          fontFamily: FONT_M,
          fontSize: 9,
          color: 'rgba(13,13,13,0.45)',
          textTransform: 'uppercase',
          letterSpacing: '0.3em',
          lineHeight: 1.4,
        }}>
          {project.chain}
        </div>
        <button
          data-testid="close-active"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontFamily: FONT_M,
            fontSize: 9,
            color: 'rgba(13,13,13,0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            padding: 0,
            cursor: 'pointer',
            flexShrink: 0,
            marginLeft: 12,
            lineHeight: 1.4,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#E8351A'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(13,13,13,0.3)'; }}
        >
          × ESC
        </button>
      </div>

      {/* Thin structural rule */}
      <div style={{
        ...reveal(40),
        width: '100%',
        height: 1,
        background: 'rgba(13,13,13,0.08)',
        marginBottom: 14,
      }} />

      {/* Project name */}
      <div style={{ ...reveal(80), marginBottom: 0 }}>
        <AsciiTitle name={project.name} frame={titleFrame} projectId={projectId} />
      </div>

      {/* Divider */}
      <div style={{
        ...reveal(160),
        width: '55%',
        height: 1,
        background: 'rgba(13,13,13,0.10)',
        marginTop: 16,
        marginBottom: 14,
      }} />

      {/* stat1 */}
      <div style={{
        ...reveal(240),
        fontFamily: FONT_M,
        fontSize: 11,
        color: 'rgba(13,13,13,0.75)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        lineHeight: 1.65,
        marginBottom: 5,
      }}>
        {project.stat1}
      </div>

      {/* stat2 */}
      <div style={{
        ...reveal(320),
        fontFamily: FONT_M,
        fontSize: 9,
        color: 'rgba(13,13,13,0.38)',
        textTransform: 'uppercase',
        letterSpacing: '0.15em',
        marginBottom: 20,
      }}>
        {project.stat2}
      </div>

      {project.link && (
        <div style={reveal(400)}>
          <CollectionLink href={project.link} projectId={projectId} label={project.ctaLabel} />
        </div>
      )}
    </div>
  );
}

/* ─── LINKS ROW — sits above CONNECT button in flex column ─── */
function LinksRow() {
  const isMobile = useIsMobile();
  return (
    <div
      data-testid="connect-links"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'center',
        rowGap: isMobile ? '10px' : '6px',
        columnGap: isMobile ? '2px' : 0,
        background: 'rgba(242,239,228,0.96)',
        padding: isMobile ? '14px 16px' : '10px 20px',
        marginBottom: 6,
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '90vw',
        opacity: 0,
        animation: 'connectReveal 0.2s steps(4) forwards',
        borderTop: '1px solid rgba(13,13,13,0.06)',
        borderLeft: '1px solid rgba(13,13,13,0.06)',
        borderRight: '1px solid rgba(13,13,13,0.06)',
      }}
    >
      {CONNECT_LINKS.flatMap((link, i) => {
        const items = [];
        if (i > 0) {
          items.push(
            <div
              key={`sep-${i}`}
              style={{ width: 1, height: 10, background: 'rgba(13,13,13,0.18)', margin: isMobile ? '0 8px' : '0 10px', flexShrink: 0 }}
            />
          );
        }
        items.push(
          <a
            key={link.label}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`connect-${link.label.toLowerCase().replace(/\./g, '-')}`}
            style={{
              fontFamily: FONT_M,
              fontSize: isMobile ? 11 : 'clamp(9px, 1.8vw, 11px)',
              color: '#0D0D0D',
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              padding: isMobile ? '6px 0' : 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#E8351A'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#0D0D0D'; }}
          >
            {link.label} ↗
          </a>
        );
        return items;
      })}
    </div>
  );
}

/* ─── LAYER 5 — CONNECT DRAWER ─── */
function ConnectDrawer({ active, connectOpen, onTrigger, onClose }) {
  const [btnHovered, setBtnHovered] = useState(false);
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        position: 'absolute',
        bottom: isMobile
          ? 'calc(20px + env(safe-area-inset-bottom, 16px))'
          : 20,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {connectOpen && <LinksRow />}

      {/* Thin structural separator */}
      <div style={{
        width: 28,
        height: 1,
        background: 'rgba(13,13,13,0.4)',
        marginBottom: 8,
      }} />

      {/* Trigger button */}
      <button
        data-testid="connect-trigger"
        onClick={(e) => { e.stopPropagation(); connectOpen ? onClose() : onTrigger(); }}
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        style={{
          display: 'block',
          border: '1px solid #0D0D0D',
          padding: isMobile ? '10px 22px' : '6px 18px',
          fontFamily: FONT_M,
          fontSize: isMobile ? 11 : 10,
          color: btnHovered ? '#F2EFE4' : '#0D0D0D',
          background: btnHovered ? '#0D0D0D' : 'transparent',
          opacity: active ? 0.08 : 1,
          textTransform: 'uppercase',
          letterSpacing: '0.28em',
          transition: 'background 0ms steps(1), color 0ms steps(1)',
          whiteSpace: 'nowrap',
          minHeight: isMobile ? 44 : 'auto',
        }}
      >
        {connectOpen ? 'CONNECT ×' : 'CONNECT +'}
      </button>
    </div>
  );
}

/* ─── MAIN V5 APP ─── */
function V5App() {
  const [hovered, setHovered]               = useState(null);
  const [active, setActive]                 = useState(null);
  const [connectOpen, setConnect]           = useState(false);
  const [paintOpen, setPaintOpen]           = useState(false);
  const [paintHovered, setPaintHovered]     = useState(false);
  const [disturbPoint, setDisturbPoint]     = useState(null);
  const [mousePos, setMousePos]             = useState({ x: -9999, y: -9999 });
  const [contentBounds, setContentBounds]   = useState(null);
  const [pillY, setPillY]                   = useState(0);
  const isMobile                            = useIsMobile();
  const imgCacheRef                         = useRef([]);
  const proximityRafRef                     = useRef(null);
  const contentRef                          = useRef(null);
  const hoveredRef                          = useRef(null);
  const pillRAFRef                          = useRef(null);
  const pillStartRef                        = useRef(null);

  /* Keep hoveredRef in sync for keyboard nav closure */
  useEffect(() => { hoveredRef.current = hovered; }, [hovered]);

  /* Clear disturbPoint after 500ms */
  useEffect(() => {
    if (!disturbPoint) return;
    const t = setTimeout(() => setDisturbPoint(null), 500);
    return () => clearTimeout(t);
  }, [disturbPoint]);

  /* Preload all poster images on mount — prevents first-hover flash */
  useEffect(() => {
    imgCacheRef.current = PROJECT_IDS.map(id => {
      const img = new Image();
      img.src = PROJECTS[id].image;
      return img;
    });
    // PROJECT_IDS and PROJECTS are module constants; imgCacheRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ESC — collapse everything */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setActive(null);
        setConnect(false);
        setHovered(null);
        return;
      }
      /* Arrow keys — cycle through project nodes */
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const cur = hoveredRef.current;
        const idx = cur ? PROJECT_IDS.indexOf(cur) : -1;
        setHovered(PROJECT_IDS[(idx + 1) % PROJECT_IDS.length]);
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const cur = hoveredRef.current;
        const idx = cur ? PROJECT_IDS.indexOf(cur) : PROJECT_IDS.length;
        setHovered(PROJECT_IDS[(idx - 1 + PROJECT_IDS.length) % PROJECT_IDS.length]);
        return;
      }
      /* Enter — open hovered node */
      if (e.key === 'Enter') {
        const cur = hoveredRef.current;
        if (cur) { setActive(cur); setConnect(false); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // setters are stable; hoveredRef is a stable ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Measure active content bounds for digit suppression ─── */
  useEffect(() => {
    setContentBounds(null);
    if (active && contentRef.current) {
      const t = setTimeout(() => {
        if (contentRef.current) {
          setContentBounds(contentRef.current.getBoundingClientRect());
        }
      }, 50);
      return () => clearTimeout(t);
    }
  }, [active]);

  /* ─── Re-measure content bounds on viewport resize ─── */
  useEffect(() => {
    if (!active) return;
    const remeasure = () => {
      setTimeout(() => {
        if (contentRef.current) {
          setContentBounds(contentRef.current.getBoundingClientRect());
        }
      }, 150);
    };
    window.addEventListener('resize', remeasure);
    return () => window.removeEventListener('resize', remeasure);
  }, [active]);

  const handleCanvasClick = () => {
    if (active) setActive(null);
    if (connectOpen) setConnect(false);
  };

  /* Proximity mousemove — RAF-throttled */
  useEffect(() => {
    const mPos = { x: -9999, y: -9999 };
    const onMove = (e) => {
      mPos.x = e.clientX;
      mPos.y = e.clientY;
      if (!proximityRafRef.current) {
        proximityRafRef.current = requestAnimationFrame(() => {
          setMousePos({ x: mPos.x, y: mPos.y });
          proximityRafRef.current = null;
        });
      }
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (proximityRafRef.current) {
        cancelAnimationFrame(proximityRafRef.current);
        proximityRafRef.current = null;
      }
    };
  }, []);

  /* Pill float — stepped sine via RAF (same formula as spec) */
  useEffect(() => {
    const animate = (ts) => {
      if (!pillStartRef.current) pillStartRef.current = ts;
      const elapsed = ts - pillStartRef.current;
      const step    = Math.floor((elapsed % 4000) / 500);
      const y       = Math.sin(step * Math.PI / 4) * 5;
      setPillY(y);
      pillRAFRef.current = requestAnimationFrame(animate);
    };
    pillRAFRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(pillRAFRef.current);
  }, []);

  return (
    <div
      data-testid="v5-root"
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: '#F2EFE4',
        position: 'relative',
      }}
      onClick={handleCanvasClick}
    >
      <CustomCursor />

      <UnifiedField hoveredProject={hovered} activeProject={active} contentBounds={contentBounds} />
      <AsciiField
        isIdle={hovered === null && active === null}
        isActive={active !== null}
        disturbPoint={disturbPoint}
      />

      <WordmarkLiving
        hoveredProject={hovered}
        activeProject={active}
      />
      <StaticMeta />

      {/* ── PAINT node — project-node treatment, mobile-aware position ── */}
      <button
        data-testid="paint-trigger"
        onClick={(e) => { e.stopPropagation(); setPaintOpen(true); }}
        onMouseEnter={() => !isMobile && setPaintHovered(true)}
        onMouseLeave={() => !isMobile && setPaintHovered(false)}
        style={{
          position:      'absolute',
          /* Mobile: centered at top-center clear of pill video */
          left:          isMobile ? '50%' : '46vw',
          top:           isMobile ? '16vh' : '44vh',
          background:    'none',
          border:        'none',
          padding:       isMobile ? '10px 0' : '4px 0',
          zIndex:        20,
          opacity:       (paintOpen || active) ? 0
                          : paintHovered          ? 1.0
                          : hovered               ? 0.12
                          : 0.65,
          transform:     isMobile
                          ? 'translateX(-50%)'
                          : paintHovered
                            ? 'translate(2px, -5px)'
                            : 'translate(0px, 0px)',
          transition:    'opacity 80ms steps(2), transform 120ms steps(3)',
          textAlign:     isMobile ? 'center' : 'left',
          pointerEvents: (paintOpen || active) ? 'none' : 'auto',
          cursor:        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Cline x1='10' y1='0' x2='10' y2='7' stroke='%23E8351A' stroke-width='1.5'/%3E%3Cline x1='10' y1='13' x2='10' y2='20' stroke='%23E8351A' stroke-width='1.5'/%3E%3Cline x1='0' y1='10' x2='7' y2='10' stroke='%23E8351A' stroke-width='1.5'/%3E%3Cline x1='13' y1='10' x2='20' y2='10' stroke='%23E8351A' stroke-width='1.5'/%3E%3Ccircle cx='10' cy='10' r='2.5' stroke='%23E8351A' stroke-width='1.5' fill='none'/%3E%3C/svg%3E\") 10 10, crosshair",
        }}
      >
        {/* Chain label */}
        <div style={{
          fontFamily:    FONT_M,
          fontSize:      isMobile ? 9 : 10,
          color:         'rgba(13,13,13,0.25)',
          textTransform: 'uppercase',
          letterSpacing: '0.25em',
          marginBottom:  2,
        }}>
          // CANVAS ·
        </div>
        {/* Node name */}
        <div style={{
          fontFamily:    FONT_M,
          fontSize:      isMobile ? 13 : (paintHovered ? 15 : 13),
          color:         '#0D0D0D',
          textTransform: 'uppercase',
          letterSpacing: '0.25em',
          transition:    'font-size 80ms steps(2)',
          lineHeight:    1.4,
          whiteSpace:    'nowrap',
        }}>
          PAINT
        </div>
      </button>

      {/* Idle hint — fades on active or connectOpen */}
      <div
        data-testid="idle-hint"
        style={{
          position: 'absolute',
          bottom: 64,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          fontFamily: FONT_M,
          fontSize: 9,
          color: '#0D0D0D',
          textTransform: 'uppercase',
          letterSpacing: '0.3em',
          opacity: (active || connectOpen) ? 0 : 0.42,
          transition: 'opacity 0.15s steps(2)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        // HOVER TO EXPLORE
      </div>

      {PROJECT_IDS.map((id, index) => (
        <ProjectNode
          key={id}
          id={id}
          hovered={hovered}
          active={active}
          onHover={(id, pos) => { setHovered(id); setDisturbPoint(pos); }}
          onLeave={() => setHovered(null)}
          onClick={(projId) => { setActive(projId); setConnect(false); }}
          isMobile={isMobile}
          index={index}
          mousePos={mousePos}
          disabled={paintOpen}
        />
      ))}

      {active && (
        <ActiveContent
          key={active}
          projectId={active}
          onClose={() => setActive(null)}
          isMobile={isMobile}
          contentRef={contentRef}
        />
      )}

      {/* Pill APNG — animates automatically, transparent bg, float via RAF */}
      <img
        data-testid="pill-video"
        src="/videos/pill.apng"
        alt=""
        style={{
          position:       'fixed',
          left:           '50%',
          top:            '50%',
          width:          'clamp(80px, 7vw, 120px)',
          height:         'auto',
          pointerEvents:  'none',
          zIndex:         35,
          opacity:        (hovered === null && active === null) ? 0.85 : 0,
          mixBlendMode:   'multiply',
          transition:     'opacity 0.3s steps(3)',
          transform:      `translate(-50%, -50%) translateY(${pillY}px)`,
        }}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
          const video        = document.createElement('video');
          video.src          = '/videos/pill.webm';
          video.autoplay     = true;
          video.muted        = true;
          video.loop         = true;
          video.playsInline  = true;
          video.style.cssText =
            'position:fixed;left:50%;top:50%;transform:translate(-50%,-50%) translateY(0px);' +
            'width:clamp(80px,7vw,120px);pointer-events:none;z-index:35;mix-blend-mode:multiply';
          document.body.appendChild(video);
        }}
      />

      <ConnectDrawer
        active={active}
        connectOpen={connectOpen}
        onTrigger={() => setConnect(true)}
        onClose={() => setConnect(false)}
      />

      {paintOpen && (
        <PaintWindow onClose={() => setPaintOpen(false)} />
      )}

    </div>
  );
}

export default function App() {
  return <V5App />;
}
